import { Link } from "@/lib/router-compat";
import { Mail, Linkedin, Twitter } from "lucide-react";
import { useTranslation } from "react-i18next";
import thiiaLogo from "@/assets/thiia-logo.svg";
import { trackNavClick, trackExternalLinkClick } from "@/lib/analytics";
import { useLegalDocs } from "@/lib/legalDocs";

export default function PublicFooter() {
  const { t } = useTranslation();
  const legalDocs = useLegalDocs();

  const internalLinkClass =
    "inline-flex items-center min-h-8 md:min-h-0 text-[hsl(270,20%,65%)] hover:text-[hsl(270,55%,72%)] transition-colors"; // min-h-8 : cible tactile ≥ 32 px en mobile
  const externalLinkClass =
    "text-[hsl(270,20%,65%)] hover:text-[hsl(270,55%,72%)] transition-colors inline-flex items-center gap-2 min-h-8 md:min-h-0";

  // Internal footer link with GA4 nav tracking. `section` distinguishes
  // product / legal navigation inside the same footer.
  const FLink = ({
    to,
    label,
    section,
  }: {
    to: string;
    label: string;
    section: "product" | "legal";
  }) => (
    <Link
      to={to}
      className={internalLinkClass}
      onClick={() => trackNavClick(`footer_${section}`, label, to)}
    >
      {label}
    </Link>
  );

  return (
    <footer className="bg-[hsl(280,35%,12%)] text-[hsl(270,30%,90%)] border-t border-[hsl(280,25%,20%)] pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-12">
          <div className="md:col-span-1 flex flex-col gap-4">
            <div className="flex items-center gap-2.5">
              <img src={thiiaLogo} alt="Thiia" className="w-8 h-8" />
              <span className="font-display font-bold text-lg text-white">Thiia</span>
            </div>
            <p className="text-sm text-[hsl(270,20%,65%)] leading-relaxed">
              {t("landing.footerTagline")}
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <h4 className="text-sm font-semibold text-white tracking-wide uppercase">{t("landing.footerProduct")}</h4>
            <nav className="flex flex-col gap-2 text-sm">
              <FLink to="/landing#features" label={t("landing.footerFeatures")} section="product" />
              <FLink to="/use-cases" label={t("landing.navUseCases")} section="product" />
              <FLink to="/landing#steps" label={t("landing.footerHow")} section="product" />
              <FLink to="/crm-sans-abonnement" label={t("noSubCrm.footerLink")} section="product" />
              <FLink to="/crm-tpe-paris" label={t("tpeParis.footerLink")} section="product" />
              <FLink to="/comparaisons" label="Comparaisons" section="product" />
              <FLink to="/docs" label={t("landing.navDocs")} section="product" />
            </nav>
          </div>

          <div className="flex flex-col gap-3">
            <h4 className="text-sm font-semibold text-white tracking-wide uppercase">{t("legalDocs.footerHeading")}</h4>
            <nav className="flex flex-col gap-2 text-sm">
              {legalDocs.map((doc) => (
                <FLink key={doc.to} to={doc.to} label={doc.label} section="legal" />
              ))}
            </nav>
          </div>

          <div className="flex flex-col gap-3">
            <h4 className="text-sm font-semibold text-white tracking-wide uppercase">{t("landing.footerContact")}</h4>
            <nav className="flex flex-col gap-2 text-sm">
              <a
                href="mailto:hello@thiia.app"
                className={externalLinkClass}
                onClick={() => trackExternalLinkClick("footer_contact", "email", "mailto:hello@thiia.app")}
              >
                <Mail className="w-4 h-4" />hello@thiia.app
              </a>
              <a
                href="https://linkedin.com/company/thiia"
                target="_blank"
                rel="noopener noreferrer"
                className={externalLinkClass}
                onClick={() => trackExternalLinkClick("footer_contact", "linkedin", "https://linkedin.com/company/thiia")}
              >
                <Linkedin className="w-4 h-4" />LinkedIn
              </a>
              <a
                href="https://x.com/thiia_ai"
                target="_blank"
                rel="noopener noreferrer"
                className={externalLinkClass}
                onClick={() => trackExternalLinkClick("footer_contact", "twitter", "https://x.com/thiia_ai")}
              >
                <Twitter className="w-4 h-4" />Twitter / X
              </a>
            </nav>
          </div>
        </div>

        <div className="border-t border-[hsl(280,25%,20%)] pt-6 flex flex-col md:flex-row items-center justify-between gap-3">
          <span className="text-xs text-[hsl(270,20%,55%)]">
            © {new Date().getFullYear()} Thiia — KREAPP SAS. {t("landing.footerCopyright")}
          </span>
          <span className="text-xs text-[hsl(270,20%,55%)]">
            {t("landing.footerCreatedBy")}{" "}
            <a
              href="https://kreapp.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block py-1.5 md:py-0 hover:text-[hsl(270,55%,72%)] transition-colors underline underline-offset-2"
              onClick={() => trackExternalLinkClick("footer_credits", "kreapp", "https://kreapp.com")}
            >
              KREAPP
            </a>{" "}
            {t("landing.footerMadeIn")}
          </span>
        </div>
      </div>
    </footer>
  );
}
